import React, { forwardRef, useEffect, useRef, useImperativeHandle, useCallback } from 'react';
import { LanguageOptionsRegistry, EditorProvider, MonacoEnvironment } from '@alipay/e2-editor-core';
import classNames from 'classnames';
import _uniq from 'lodash-es/uniq';
import ThemeModule from '@/theme';
import useDeepCompareEffect from '@/hooks/useDeepCompareEffect';
import { needLoadSqlService } from '@/utils';
import { ParentNodeResizeDetector } from '@/utils/resize-detector';
import './index.scss';
const defaultOptions = {
    theme: 'white',
    suggestLineHeight: 24,
    automaticLayout: false,
    fontSize: 14,
    lineHeight: 20,
    folding: true,
    wordWrap: 'on',
    lineDecorationsWidth: 0,
    lineNumbersMinChars: 3,
    readOnly: false,
    padding: {
        top: 12,
        bottom: 12,
    },
};
const CnCodeEditorCore = forwardRef((props, ref) => {
    const { className, style, editorOptions, onCreated, onChange, sqlOptions, language, value, supportLanguages, } = props;
    const { monaco } = window;
    const mergedSupportLanguages = _uniq([...(supportLanguages || []), language]);
    const editorContainer = useRef(null);
    const editorInstance = useRef();
    const sqlRegistry = useRef();
    useImperativeHandle(ref, () => {
        return {
            addAction: doAddAction,
            format: doFormat,
        };
    });
    const doAddAction = (descriptor) => {
        const codeEditor = editorInstance.current?.codeEditor;
        return codeEditor?.addAction(descriptor);
    };
    // 格式化
    const doFormat = () => {
        const codeEditor = editorInstance.current?.codeEditor;
        if (!codeEditor) {
            return Promise.resolve(false);
        }
        const selection = codeEditor.getSelection();
        const hasSelection = selection && !selection.isEmpty();
        const action = codeEditor.getAction(hasSelection ? 'editor.action.formatSelection' : 'editor.action.formatDocument');
        if (action) {
            return new Promise((resolve, reject) => {
                action.run().then(() => {
                    resolve(true);
                }, (err) => {
                    reject(err);
                });
            });
        }
        return Promise.reject(new Error('format not support'));
    };
    const resetValue = () => {
        if (!editorInstance.current) {
            return;
        }
        const model = editorInstance.current.codeEditor.getModel();
        if (!model) {
            return;
        }
        const endLineNumber = model.getLineCount();
        const endColumn = model.getLineMaxColumn(endLineNumber);
        editorInstance.current.codeEditor.executeEdits('', [
            { text: model.getValue(), range: new monaco.Range(1, 1, endLineNumber, endColumn) },
        ]);
    };
    const clearCache = () => {
        sqlRegistry.current
            ?.getSQLModeByLanguage(language || '')
            ?.getSQLServiceCacheManager()
            ?.CLEAR_ALL_CACHES();
        resetValue();
    };
    const registerLanguage = () => {
        MonacoEnvironment.loadModule(async (container) => {
            const { loadModules } = await import('@/loader');
            await loadModules(container, mergedSupportLanguages);
            // 加载自定义主题
            container.load(ThemeModule);
        });
        MonacoEnvironment.init().then(async () => {
            if (!editorContainer.current) {
                return;
            }
            if (sqlOptions) {
                const OptionsRegistry = MonacoEnvironment.container.get(LanguageOptionsRegistry);
                OptionsRegistry.registerOptions(`${language}-sql-service`, sqlOptions);
            }
            // TODO：暂时通过约定式判断是否加载sqlService
            if (needLoadSqlService(mergedSupportLanguages)) {
                const sqlService = await import('@alipay/e2-language-sql-service');
                sqlRegistry.current = MonacoEnvironment.container.get(sqlService.SQLModeRegistry);
                sqlRegistry.current.init();
            }
            const editorProvider = MonacoEnvironment.container.get(EditorProvider);
            editorInstance.current = await editorProvider.create(editorContainer.current, {
                ...defaultOptions,
                ...editorOptions,
                language,
                value,
            });
            if (onChange) {
                editorInstance.current.codeEditor.onDidChangeModelContent(() => {
                    editorInstance.current && onChange(editorInstance.current.codeEditor.getValue());
                });
            }
            if (onCreated) {
                onCreated(editorInstance.current.codeEditor, clearCache);
            }
        });
    };
    // 监听事件父元素resize
    const onResize = useCallback(() => {
        const codeEditor = editorInstance.current?.codeEditor;
        if (codeEditor) {
            codeEditor.layout();
        }
    }, []);
    useEffect(() => {
        registerLanguage();
        const detector = new ParentNodeResizeDetector(editorContainer.current, onResize);
        return () => {
            editorInstance.current?.codeEditor?.dispose();
            detector.uninstallElementResizeDetector();
        };
    }, []);
    useEffect(() => {
        if (!editorInstance.current) {
            return;
        }
        // 更新编辑器内容
        if (editorInstance.current.codeEditor.getValue() !== value) {
            editorInstance.current.codeEditor.setValue(value || '');
        }
    }, [value]);
    useEffect(() => {
        if (!editorInstance.current) {
            return;
        }
        const curModel = editorInstance.current.codeEditor.getModel();
        // 语言切换
        if (language && curModel?.getLanguageId() !== language) {
            monaco.editor.setModelLanguage(editorInstance.current.codeEditor.getModel(), language);
        }
    }, [language]);
    useDeepCompareEffect(() => {
        if (!editorInstance.current) {
            return;
        }
        // 更新配置
        editorInstance.current.codeEditor.updateOptions({
            ...defaultOptions,
            ...editorOptions,
        });
    }, [editorOptions]);
    useDeepCompareEffect(() => {
        const sqlMode = sqlRegistry.current?.getSQLModeByLanguage(language || '');
        if (sqlMode) {
            const newSQLOptions = sqlOptions || {};
            MonacoEnvironment.container
                .get(LanguageOptionsRegistry)
                .registerOptions(`${language}-sql-service`, { ...newSQLOptions });
            sqlMode.registerSQLOptions(newSQLOptions);
            sqlMode.updateValidateFlag(newSQLOptions.enableValidate || false);
        }
    }, [language, sqlOptions]);
    return (<div className={classNames('cn-editor-core', className)} style={style} ref={editorContainer}/>);
});
CnCodeEditorCore.displayName = 'CnCodeEditorCore';
export { CnCodeEditorCore };
